import { useState, useEffect } from 'react'
import { PrinterIcon } from '@heroicons/react/24/outline'
import { useToast } from '../../contexts/ToastContext'

const API_BASE_URL = '/api/v1/dev'

interface Printer {
  id: number
  key: string
  name: string
  mode: 'local' | 'ipp' | 'cups'
  ipp_uri: string | null
  cups_host: string | null
  cups_port: number | string | null
  cups_queue: string | null
}

export default function Printers() {
  const toast = useToast()
  const [printers, setPrinters] = useState<Printer[]>([])
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [testingKey, setTestingKey] = useState<string | null>(null)

  const fetchPrinters = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/printers`, { headers: { Accept: 'application/json' } })
      const data = await res.json()
      if (res.ok && data.success) setPrinters(data.data)
    } catch {
      toast.error('No se pudieron cargar las impresoras')
    }
  }

  useEffect(() => { fetchPrinters() }, [])

  const setField = (key: string, field: keyof Printer, value: string) => {
    setPrinters(prev => prev.map(p => p.key === key ? { ...p, [field]: value } : p))
  }

  const save = async (p: Printer) => {
    setSavingKey(p.key)
    try {
      const res = await fetch(`${API_BASE_URL}/printers/${p.key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ mode: p.mode, ipp_uri: p.ipp_uri, cups_host: p.cups_host, cups_port: p.cups_port, cups_queue: p.cups_queue }),
      })
      const data = await res.json()
      if (res.ok && data.success) {
        toast.success(data.message || 'Impresora guardada')
      } else {
        toast.error(data.message || 'Error al guardar')
      }
    } catch {
      toast.error('Error de conexión')
    } finally {
      setSavingKey(null)
    }
  }

  const test = async (p: Printer) => {
    setTestingKey(p.key)
    try {
      const res = await fetch(`${API_BASE_URL}/printers/${p.key}/test`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      })
      const data = await res.json()
      if (res.ok && data.success) {
        toast.success(data.message || 'Prueba enviada')
      } else {
        toast.error(data.message || 'Error en la prueba')
      }
    } catch {
      toast.error('Error de conexión')
    } finally {
      setTestingKey(null)
    }
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <PrinterIcon className="h-7 w-7 text-primary-600" />
          Impresoras
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Configura cómo se imprime cada documento. <b>Local</b>: se abre el PDF y se imprime desde el navegador.
          <b> IPP</b>: el servidor envía el PDF directamente a la impresora (IPP Everywhere).
          <b> CUPS</b>: a través de una cola del servidor de impresión.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {printers.map(p => (
          <div key={p.key} className="bg-white border border-gray-200 rounded-md p-4 space-y-3">
            <div className="font-medium text-gray-900">{p.name}</div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modo</label>
              <select
                value={p.mode}
                onChange={(e) => setField(p.key, 'mode', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="local">Local (navegador)</option>
                <option value="ipp">IPP directo (IPP Everywhere)</option>
                <option value="cups">CUPS (cola del servidor)</option>
              </select>
            </div>

            {p.mode === 'ipp' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URI IPP</label>
                <input
                  type="text"
                  value={p.ipp_uri || ''}
                  onChange={(e) => setField(p.key, 'ipp_uri', e.target.value)}
                  placeholder="ipp://192.168.1.50:631/ipp/print"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm placeholder:italic placeholder:text-gray-300"
                />
              </div>
            )}

            {p.mode === 'cups' && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Host CUPS</label>
                    <input
                      type="text"
                      value={p.cups_host || ''}
                      onChange={(e) => setField(p.key, 'cups_host', e.target.value)}
                      placeholder="localhost"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm placeholder:italic placeholder:text-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Puerto</label>
                    <input
                      type="number"
                      value={p.cups_port ?? ''}
                      onChange={(e) => setField(p.key, 'cups_port', e.target.value)}
                      placeholder="631"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm placeholder:italic placeholder:text-gray-300"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cola</label>
                  <input
                    type="text"
                    value={p.cups_queue || ''}
                    onChange={(e) => setField(p.key, 'cups_queue', e.target.value)}
                    placeholder="nombre_de_la_cola"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm placeholder:italic placeholder:text-gray-300"
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 pt-2">
              {p.mode !== 'local' && (
                <button
                  onClick={() => test(p)}
                  disabled={testingKey === p.key}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  {testingKey === p.key ? 'Enviando…' : 'Probar'}
                </button>
              )}
              <button
                onClick={() => save(p)}
                disabled={savingKey === p.key}
                className="px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {savingKey === p.key ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
